import type * as Immutable from 'immutable';
import { createMigration } from './utils';

/**
 * Backfills `creationSource: 'sceneDesign'` on every animation configuration. The new
 * `creationSource` field tells the animation on/off toggle which animations it owns: turning
 * animations "off" disables `sceneDesign` animations and enables `popIn` animations. Every
 * animation that exists today was created by a scene design, so they are all tagged `sceneDesign`.
 *
 * Animation configurations live in several places on an asset:
 *   - `objectAnimation` (asset-level object animations)
 *   - `{normalStyle,highlightStyle,spokenWordStyle}.animationConfigurations` (text style animations)
 *   - `{normalStyle,highlightStyle,spokenWordStyle}.graphics[].animationConfigurations`
 *   - `{colorGraphics,alphaGraphics}[].animation` (legacy graphic animations)
 *
 * `migrateScene` is self-contained so external callers using `Migrator.updateSceneData` /
 * `updateSceneDesignData` get the same result. SceneGroup caption assets are migrated in a private
 * `migrateSceneGroupCaptions` helper called from `migrateVideo`.
 */

const SCENE_DESIGN_CREATION_SOURCE = 'sceneDesign';

const STYLE_FIELDS = ['normalStyle', 'highlightStyle', 'spokenWordStyle'] as const;

const LEGACY_GRAPHIC_FIELDS = ['colorGraphics', 'alphaGraphics'] as const;

function backfillAnimationList(
    map: Immutable.Map<string, unknown>,
    path: Array<string | number>,
): void {
    const list = map.getIn(path) as Immutable.List<Immutable.Map<string, unknown>> | undefined;
    if (!list) return;
    list.forEach((_anim, i) => {
        map.setIn([...path, i, 'creationSource'], SCENE_DESIGN_CREATION_SOURCE);
    });
}

function backfillAsset(
    map: Immutable.Map<string, unknown>,
    assetPath: Array<string | number>,
): void {
    const asset = map.getIn(assetPath) as Immutable.Map<string, unknown> | undefined;
    if (!asset) return;

    if (asset.get('objectAnimation')) {
        backfillAnimationList(map, [...assetPath, 'objectAnimation']);
    }

    STYLE_FIELDS.forEach((styleField) => {
        const style = asset.get(styleField) as Immutable.Map<string, unknown> | undefined;
        if (!style) return;

        if (style.get('animationConfigurations')) {
            backfillAnimationList(map, [...assetPath, styleField, 'animationConfigurations']);
        }

        const graphics = style.get('graphics') as Immutable.List<unknown> | undefined;
        if (graphics) {
            graphics.forEach((_graphic, gIndex) => {
                const graphic = map.getIn([...assetPath, styleField, 'graphics', gIndex]) as
                    | Immutable.Map<string, unknown>
                    | undefined;
                if (graphic && graphic.get('animationConfigurations')) {
                    backfillAnimationList(map, [
                        ...assetPath,
                        styleField,
                        'graphics',
                        gIndex,
                        'animationConfigurations',
                    ]);
                }
            });
        }
    });

    LEGACY_GRAPHIC_FIELDS.forEach((graphicField) => {
        const graphics = asset.get(graphicField) as Immutable.List<unknown> | undefined;
        if (!graphics) return;
        graphics.forEach((_graphic, gIndex) => {
            const graphic = map.getIn([...assetPath, graphicField, gIndex]) as
                | Immutable.Map<string, unknown>
                | undefined;
            if (graphic && graphic.get('animation')) {
                backfillAnimationList(map, [...assetPath, graphicField, gIndex, 'animation']);
            }
        });
    });
}

function backfillChildren(
    map: Immutable.Map<string, unknown>,
    childrenPath: Array<string | number>,
): void {
    const children = map.getIn(childrenPath) as Immutable.List<unknown> | undefined;
    if (!children) return;
    children.forEach((_child, i) => {
        backfillAsset(map, [...childrenPath, i]);
    });
}

/**
 * Backfills a sceneGroup's caption asset and decorations. Called from `migrateVideo` because
 * sceneGroup data has no external migration entrypoint.
 */
function migrateSceneGroupCaptions(
    sceneGroup: Immutable.Map<string, unknown>,
): Immutable.Map<string, unknown> {
    return sceneGroup.withMutations((map) => {
        if (map.getIn(['captions', 'asset'])) {
            backfillAsset(map, ['captions', 'asset']);
        }
        if (map.getIn(['captions', 'decorations'])) {
            backfillChildren(map, ['captions', 'decorations']);
        }
    });
}

export const migrationTo9_242 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            const sceneGroups = map.get('sceneGroups') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (sceneGroups) {
                sceneGroups.forEach((sg, sgIdx) => {
                    map.setIn(['sceneGroups', sgIdx], migrateSceneGroupCaptions(sg));
                });
            }

            const slides = map.get('slides') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (slides) {
                slides.forEach((scene, sceneIdx) => {
                    map.setIn(['slides', sceneIdx], this.migrateScene(scene));
                });
            }
        });
    },

    migrateScene(scene, _isSceneDesign) {
        return scene.withMutations((map) => {
            backfillChildren(map, ['children']);
            backfillChildren(map, ['stickers']);

            const subslides = map.get('subslides') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (subslides) {
                subslides.forEach((_subslide, subIdx) => {
                    backfillChildren(map, ['subslides', subIdx, 'children']);
                });
            }

            const presets = map.get('textPositionPresets') as
                | Immutable.Map<string, Immutable.List<unknown>>
                | undefined;
            if (presets) {
                presets.forEach((_assets, position) => {
                    backfillChildren(map, ['textPositionPresets', position]);
                });
            }
        });
    },
});
